import React from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge, Ico, Icons } from './index';
import { useApp } from '../../context/AppContext';

export default function TournamentCard({ tournament, showActions = false }) {
  const { currentUser } = useApp();
  const t = tournament;
  const surfaceColors = { 'Hard Court': '#1E88E5', 'Clay': '#ffb68f', 'Grass': '#43A047' };
  const surfaceColor = surfaceColors[t.surface] || '#8892A6';
  const fillPct = (t.registeredPlayers.length / t.maxPlayers) * 100;
  const dateStr = new Date(t.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endStr = new Date(t.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const seededCount = (t.seeds || []).length;
  const isDone = t.status === 'completed';
  
  // If current user is the organizer of this tournament, link to management page
  const isOrganizer = currentUser?.role === 'organizer' && String(currentUser?.id) === String(t.organizerId);
  const linkTo = isOrganizer ? `/organizer/tournament/${t.id}` : `/tournaments/${t.id}`;

  return (
    <Link to={linkTo} style={{ textDecoration: 'none' }}>
      <div className="t-card" style={{ boxShadow: `0 0 2px 0.5px ${surfaceColor}`, borderRadius: '12px', overflow: 'hidden', background: 'var(--card-bg)' }}>
        {/* Header band */}
        <div className="t-header" style={{ background: `linear-gradient(135deg, ${surfaceColor}18 0%, ${surfaceColor}08 100%)` }}>
          <div className="t-header-top">
            <StatusBadge status={t.status} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="t-surface-tag">{t.matchType || 'Singles'}</span>
              <span className="t-surface-tag">{t.surface}</span>
            </div>
          </div>
          <h2 className="t-name" style={{ fontSize: 18, letterSpacing: '0.3px', color: 'var(--text-primary)' }}>{t.name}</h2>
          {t.description && <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '4px 0 0', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', opacity: 0.7 }}>{t.description}</p>}
          <div className="t-meta" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
            <Ico d={Icons.pin} size={12} color="var(--text-secondary)" />
            <span>{t.location}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <span className="t-surface-tag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Ico d={Icons.calendar} size={10} color="var(--text-secondary)" />
              {dateStr} — {endStr}
            </span>
            <span className="t-surface-tag" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Ico d={Icons.users} size={10} color="var(--text-secondary)" />
              {t.registeredPlayers.length}/{t.maxPlayers} {t.matchType === 'Doubles' ? 'teams' : 'players'}
            </span>
          </div>
          {(seededCount > 0 || t.bracketGenerated || (t.entryFee > 0 && !isDone)) && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {seededCount > 0 && (
                <span className="t-surface-tag" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(67,160,71,0.1)', borderColor: 'rgba(67,160,71,0.25)', color: '#43A047' }}>
                  <Ico d={Icons.target} size={10} color="#43A047" />
                  {seededCount} seeded
                </span>
              )}
              {t.bracketGenerated && (
                <span className="t-surface-tag" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(67,160,71,0.1)', borderColor: 'rgba(67,160,71,0.25)', color: '#43A047' }}>
                  <Ico d={Icons.check} size={10} color="#43A047" />
                  Draw Generated
                </span>
              )}
              {t.entryFee > 0 && !isDone && (
                <span className="t-surface-tag" style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(251,140,0,0.1)', borderColor: 'rgba(251,140,0,0.25)', color: '#FB8C00', fontWeight: 800 }}>
                  Entry Fee: ₹{t.entryFee.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          )}
          {isDone && t.winnerName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14, borderTop: '1px solid var(--gray-100)', paddingTop: 14 }}>
              <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg, #FFD700, #FFA500)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 12px rgba(255,215,0,0.4)' }}>
                <Ico d={Icons.trophy} size={20} color="#1a202c" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,140,0,0.7)', letterSpacing: 1.2, fontFamily: "'Outfit', sans-serif", marginBottom: 2 }}>CHAMPION</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#FF8C00', fontFamily: "'Outfit', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.winnerName}</div>
              </div>
            </div>
          )}
        </div>

        {/* Body — hidden for completed */}
        {!isDone && (
          <div className="t-body" style={showActions ? { paddingBottom: 60 } : {}}>
            <div className="t-stats">
              <div className="t-stat-item"><div className="t-stat-val">{t.prize}</div><div className="t-stat-lbl">Prize Pool</div></div>
              <div className="t-stat-div" />
              <div className="t-stat-item"><div className="t-stat-val">{t.winnerPrize || 'TBD'}</div><div className="t-stat-lbl"><Ico d={Icons.trophy} size={11} color="var(--text-secondary)" /> Winner</div></div>
              <div className="t-stat-div" />
              <div className="t-stat-item"><div className="t-stat-val">{t.runnerUpPrize || 'TBD'}</div><div className="t-stat-lbl"><Ico d={Icons.medal} size={11} color="var(--text-secondary)" /> Runner-Up</div></div>
            </div>
            <div className="t-bottom">
              <div className="t-bar-wrap">
                <div className="bar"><div className="bar-fill" style={{ width: `${fillPct}%`, background: fillPct >= 90 ? '#E53935' : surfaceColor }} /></div>
                <span className="t-bar-label">{Math.round(fillPct)}% filled</span>
              </div>
            </div>
            {t.registrationDeadline && t.status === 'upcoming' && (() => {
              const dl = new Date(t.registrationDeadline);
              const daysLeft = Math.ceil((dl - new Date()) / (1000 * 60 * 60 * 24));
              if (daysLeft < 0) return null;
              return <div style={{ fontSize: 10, fontWeight: 700, color: daysLeft <= 3 ? '#E53935' : '#FB8C00', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}><Ico d={Icons.calendar} size={10} color={daysLeft <= 3 ? '#E53935' : '#FB8C00'} /> Register by {dl.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} · {daysLeft}d left</div>;
            })()}
            {t.contactName && (
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Ico d={Icons.phone} size={10} color="var(--text-secondary)" /> Contact: {t.contactName}
              </div>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
